import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, BookOpen, School, Award, ArrowUpRight } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { EDUCATION_IMAGE } from "@/lib/constants";

const CATEGORIES = [
  {
    icon: BookOpen,
    title: "Coaching & Learning Centres",
    desc: "Competitive exam prep, entrance coaching, skill institutes and certifications.",
  },
  {
    icon: GraduationCap,
    title: "Tutors & Independent Teachers",
    desc: "Transparent payments to private tutors, freelance educators and instructors.",
  },
  {
    icon: School,
    title: "Schools, Colleges & Universities",
    desc: "Semester fees, admission charges, tuition bills and hostel payments.",
  },
  {
    icon: Award,
    title: "Professional & Language Training",
    desc: "Workshops, executive education, language training and upskilling programs.",
  },
];

export const Education = () => {
  return (
    <section
      id="education"
      data-testid={LANDING.educationSection}
      className="relative py-24 md:py-32 bg-white border-y border-[#E5E5E0]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: Image + accent */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5] bg-[#FEF0EB]">
              <img
                src={EDUCATION_IMAGE}
                alt="University students learning"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md border border-white rounded-2xl p-5">
                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#F25C2A]">
                  Marketplace · Education
                </p>
                <p className="mt-2 font-serif text-lg leading-snug text-[#0A0A0A]">
                  Verified educators &amp; institutes onboard as sellers — payers
                  discover them and settle fees in a few taps.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#F25C2A]">
              · Education Fee Payments
            </p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#0A0A0A]">
              A marketplace for
              <br />
              <em className="italic text-[#F25C2A] font-normal">education fees</em>.
            </h2>
            <p className="mt-5 max-w-xl text-lg text-[#4A4A4A] leading-relaxed">
              Discover verified coaching centres, tutors, schools, colleges and
              professional training providers. Pay using your preferred method
              — credit card, prepaid card, wallet, EMI or UPI — funds settle
              cleanly to the seller&apos;s bank account once KYC is complete.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CATEGORIES.map((c, i) => (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.45 }}
                  data-testid={`${LANDING.educationCard}-${i}`}
                  className="group relative bg-[#FAFAF7] border border-[#E5E5E0] rounded-2xl p-5 hover:border-[#F25C2A] hover:bg-white transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-[#E5E5E0] text-[#F25C2A]">
                      <c.icon size={18} />
                    </span>
                    <ArrowUpRight
                      size={16}
                      className="text-[#737373] group-hover:text-[#F25C2A] transition-colors"
                    />
                  </div>
                  <h3 className="mt-4 font-serif text-lg font-medium leading-snug text-[#0A0A0A]">
                    {c.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-[#4A4A4A] leading-relaxed">
                    {c.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
